import React, { useCallback, useRef, Fragment } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ImageAd from '../ImageAd'
import IframeAd from '../IframeAd'
import HtmlAd from '../HtmlAd'
import PropTypes from 'prop-types'
import { getAdsViewTrackingStatus } from 'src/features/viewability/ViewabilitySelector'
import * as viewabilityActions from 'src/features/viewability/ViewabilitySlice'
import { arrayify } from 'src/utils'
import { mediaTypes } from 'src/services/Ads/constants/adTypes'
import useAdBlocker from 'src/hooks/useAdBlocker'

// Code shared from phoenix team for viewability
const AdAiMatch = ({
  emptyContent = false,
  mediaType,
  onAdClick = () => {},
  ChildComponent,
  btnStyles = {},
  ...adProps
}) => {
  const { viewUrl: url, area, id, src } = adProps

  const trackedData = {
    area,
    id,
    src,
    url
  }

  let viewTracked = useSelector(getAdsViewTrackingStatus({ ...trackedData }))
  const dispatch = useDispatch()
  const [baitRef, adBlocked] = useAdBlocker({
    testUrl: src
  })
  const adObserverRef = useRef()
  const adCountedRef = useRef(false)
  const adElementRef = useCallback(
    node => {
      if (
        url === undefined ||
        area === undefined ||
        src === undefined ||
        id === undefined ||
        (adCountedRef.current !== undefined &&
          adCountedRef.current == `ad-${area}-${id}-${src}`) ||
        viewTracked ||
        adBlocked
      )
        return
      if (adObserverRef.current) adObserverRef.current.disconnect()
      adObserverRef.current = new IntersectionObserver(entries => {
        if (
          entries[0].isIntersecting &&
          (!adCountedRef.current ||
            adCountedRef.current != `ad-${area}-${id}-${src}`) &&
          url !== undefined &&
          !viewTracked
        ) {
          dispatch(viewabilityActions.adsViewTrackRequest({ ...trackedData }))
          adCountedRef.current = `ad-${area}-${id}-${src}`
        }
      })
      if (node) adObserverRef.current.observe(node)
    },
    [dispatch, viewTracked, trackedData]
  )

  const getAdComponent = () => {
    // If there is a component passed down give priority
    if (ChildComponent) {
      return <ChildComponent {...adProps} />
    }

    for (let mediaType of arrayify(mediaType)) {
      switch (mediaType) {
        case mediaTypes.IMAGE:
          return <ImageAd {...adProps} />
        case mediaTypes.IFRAME:
          return <IframeAd {...adProps} />
        case mediaTypes.HTML:
          return <HtmlAd {...adProps} />
        default:
          return <></>
      }
    }
  }

  if (adBlocked) {
    return null
  }

  return (
    <Fragment>
      {/** START: Bait element for ad-block detection */}
      <div
        ref={baitRef}
        className="adsbox"
        style={{
          position: 'absolute',
          top: '-999px',
          width: '1px',
          height: '1px'
        }}
      />
      {/** END: Bait element for ad-block detection */}
      <div ref={adElementRef} />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={onAdClick}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onAdClick(e)
            }
          }}
          style={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            textAlign: 'inherit',
            width: mediaType === mediaTypes.HTML ? '100%' : 'auto',
            ...btnStyles
          }}
        >
          {emptyContent !== undefined &&
            emptyContent === false &&
            getAdComponent()}
        </button>
      </div>
    </Fragment>
  )
}

AdAiMatch.propTypes = {
  /**
   * Boolean to determine if ad is empty or not to validate its visibility
   */
  emptyContent: PropTypes.bool,
  /**
   * Type of the ad (image,iframe,etc.)
   */
  mediaType: PropTypes.string,
  /**
   * Used as data-sasia-view-url image prop for viewability purposes
   */
  viewUrl: PropTypes.string,
  /**
   * Ad area defined in aimatch request
   */
  area: PropTypes.string,
  /**
   * HTML id for the ad element container
   */
  id: PropTypes.string,
  /**
   * Source of the ad
   */
  src: PropTypes.string,
  /**
   * Used for setting the img styles
   */
  imgStyle: PropTypes.object,
  /**
   * Callback function to be called when the ad is clicked
   */
  onAdClick: PropTypes.func,
  /**
   * Custom component to be rendered instead of the default ad component
   */
  ChildComponent: PropTypes.elementType
}

export default AdAiMatch
